import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, AlertCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import { z } from "zod";
import { useSignIn } from "@empite/session";
import { applyApiFieldErrors, useZodForm } from "@empite/app-kit";
import { AuthShell } from "../ui/auth-shell";
import { Button } from "../ui/button";
import { Field } from "../ui/field";
import { Notice } from "../ui/notice";
import { UiLink } from "../ui/router";
import { errorMessage } from "./guards";
import { pendingAuthorize, safeReturnPath } from "../oauth";

const schema = z.object({
  email: z.string().min(1, "Enter your email address.").email("That is not an email address."),
  password: z.string().min(1, "Enter your password."),
});

type Values = z.infer<typeof schema>;

export interface SignInSearch {
  /** Where to go once signed in. A same-origin path, checked before use, and
   *  also where the pending authorize request is read from — see
   *  pendingAuthorize. */
  next?: string;
  /** The application whose authorize request sent the visitor here. Explicit
   *  here for a caller that has it to hand; the ordinary redirect carries it
   *  inside `next` instead. */
  client_id?: string;
}

export function SignIn({ search }: { search: SignInSearch }) {
  const navigate = useNavigate();
  const signIn = useSignIn();
  const [formError, setFormError] = useState<unknown>(null);
  const [revealed, setRevealed] = useState(false);
  const form = useZodForm<Values>(schema, { defaultValues: { email: "", password: "" } });

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      // The application AND its company travel with the sign-in, so the
      // session opens against the suite that application belongs to. Without
      // them the session is scoped to the platform's own tenant and the
      // authorize this visitor is on their way back to is refused for a suite
      // mismatch — a failure with no message a person can act on.
      //
      // The explicit search parameter wins over the one parsed out of `next`:
      // a caller that named a client meant that client. In the ordinary
      // redirect only `next` is set, which is why it is read at all.
      const pending = pendingAuthorize(search.next);
      await signIn.mutateAsync({
        ...values,
        client_id: search.client_id ?? pending.clientID,
        tenant: pending.tenant,
      });
    } catch (err) {
      if (!applyApiFieldErrors(form.setError, err, ["email", "password"])) {
        setFormError(err);
      }
      return;
    }
    const next = safeReturnPath(search.next);
    if (next) {
      // A full navigation, not a router push: `next` is an opaque path with
      // its own query string, and reloading also picks up the fresh cookie.
      window.location.assign(next);
      return;
    }
    await navigate({ to: "/" });
  });

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to your Empite Suite account."
      footer={
        <p className="text-center text-sm text-ink-3">
          No account yet?{" "}
          <UiLink
            href="/register"
            className="font-semibold text-brand hover:text-brand-strong"
          >
            Create one
          </UiLink>
          .
        </p>
      }
    >
      <form onSubmit={submit} noValidate className="mt-8 space-y-4">
        {formError ? (
          <Notice
            tone="danger"
            title="Sign-in failed"
            icon={<AlertCircle className="size-4" aria-hidden />}
          >
            {errorMessage(formError)}
          </Notice>
        ) : null}

        <Field
          label="Email"
          type="email"
          autoComplete="username"
          placeholder="you@example.com"
          error={form.formState.errors.email?.message}
          {...form.register("email")}
        />

        <Field
          label="Password"
          // The reveal toggle swaps the input's type in place, which is why it
          // lives in the field's suffix rather than beside it: the browser's
          // own password manager keeps tracking the same element.
          type={revealed ? "text" : "password"}
          autoComplete="current-password"
          error={form.formState.errors.password?.message}
          suffix={
            <button
              type="button"
              onClick={() => setRevealed((shown) => !shown)}
              aria-label={revealed ? "Hide password" : "Show password"}
              aria-pressed={revealed}
              className="grid size-7 place-items-center rounded-md text-ink-4 transition-colors hover:text-ink-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              {revealed ? (
                <EyeOff className="size-4" aria-hidden />
              ) : (
                <Eye className="size-4" aria-hidden />
              )}
            </button>
          }
          {...form.register("password")}
        />

        <Button type="submit" className="w-full" size="lg" disabled={signIn.isPending}>
          {signIn.isPending ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Signing in…
            </>
          ) : (
            <>
              Sign in
              <ArrowRight className="size-4" aria-hidden />
            </>
          )}
        </Button>
      </form>
    </AuthShell>
  );
}
